/**
 * Track Status tab of the Delivery Config page. Edits the `track_status` section
 * of the per-facility delivery-steps document: run conditions in the layout and
 * general/batch settings in a Settings modal. Saves back via a partial Firestore write.
 */
import { useEffect, useMemo, useState } from "react";
import { Button, openModal } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import QueryBuilder from "../../../../../components/queryBuilder/QueryBuilder";
import {
  CmmInputUniqueValuesProvider,
  useCmmInputUniqueValuesContext,
} from "../../../../../context/cmmInputUniqueValuesContext";
import { buildDeliveryStepConditionConfig } from "../shared/deliveryStepConditionConfig";
import StepSettingsModal from "../shared/stepSettingsModal";
import { useTrackStatusConfig } from "./useTrackStatusConfig";

const TRACK_STATUS_SETTINGS_MODAL_ID = "track-status-settings-modal";

const TrackStatusConfigInner = () => {
  const {
    form,
    loading,
    saving,
    error,
    isDirty,
    setQuery,
    applySettingsForm,
    handleDiscard,
    handleSave,
  } = useTrackStatusConfig();

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const conditionConfig = useMemo(
    () => buildDeliveryStepConditionConfig(cmmInputUniqueValues),
    [cmmInputUniqueValues],
  );

  const [resetKey, setResetKey] = useState(0);
  const [settingsModalKey, setSettingsModalKey] = useState(0);
  const [shouldOpenSettings, setShouldOpenSettings] = useState(false);

  useEffect(() => {
    if (!shouldOpenSettings) {
      return;
    }
    openModal(TRACK_STATUS_SETTINGS_MODAL_ID);
    setShouldOpenSettings(false);
  }, [shouldOpenSettings, settingsModalKey]);

  const onDiscard = () => {
    handleDiscard();
    setResetKey((k) => k + 1);
    setSettingsModalKey((k) => k + 1);
  };

  const openSettings = () => {
    setSettingsModalKey((k) => k + 1);
    setShouldOpenSettings(true);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading track status configuration..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
      <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="flex flex-shrink-0 flex-row items-start justify-between gap-4 border-b border-primaryGray-16 pb-4">
          <div className="flex flex-col">
            <div className="text-h11 font-bold text-primaryGray-1">
              Track Status
            </div>
            <div className="mt-1 text-small text-primaryGray-4">
              Configure when the track-status delivery step runs. Use Settings
              for general options and batch tuning.
            </div>
          </div>
          <Button
            buttonType="secondary"
            size="medium"
            onClick={openSettings}
            disabled={Boolean(error)}
            children="Settings"
          />
        </div>

        {error ? (
          <p className="text-body1 text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <div className="mb-1 mt-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
            <StepSettingsModal
              key={`settings-modal-${settingsModalKey}`}
              dialogId={TRACK_STATUS_SETTINGS_MODAL_ID}
              idPrefix="track-status"
              title="Track Status Settings"
              enabled={form.enabled}
              action={form.action}
              statusField={form.statusField}
              settings={form.settings}
              onApply={applySettingsForm}
            />

            <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
              <div className="text-sm font-semibold text-primaryGray-1">
                Run Conditions
              </div>
              <div className="text-tiny text-primaryGray-8">
                This step runs when the conditions below evaluate to true.
              </div>
              <QueryBuilder
                key={`qb-${resetKey}`}
                value={form.query}
                onChange={setQuery}
                config={conditionConfig}
              />
            </div>
          </div>
        )}

        <div className="flex flex-shrink-0 flex-row items-center justify-end gap-2 border-t border-primaryGray-16 pt-2">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={onDiscard}
            disabled={!isDirty || saving}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>
    </div>
  );
};

/** Track Status tab wrapped with the CMM value-search context the QueryBuilder needs. */
const TrackStatusConfig = () => (
  <CmmInputUniqueValuesProvider>
    <TrackStatusConfigInner />
  </CmmInputUniqueValuesProvider>
);

export default TrackStatusConfig;
